/**
 * Ziele-Repository.
 *
 * Key Results leben als Array IM Ziel, nicht in einer eigenen Tabelle: sie
 * werden immer gemeinsam mit ihrem Ziel gelesen, geschrieben und geloescht.
 * Eine eigene Tabelle brauchte nur Fremdschluesselpflege und brachte keine
 * einzige Abfrage, die es nicht schon gibt.
 */
import { db } from '@/db/schema';
import { nowInstant, today as heute } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import { containsDate } from '@/domain/goals/period';
import { applyLinkRule } from '@/db/repositories/links';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { DEFAULT_PROJECT_PRIORITY } from '@/domain/priority';
import { mitMessung } from '@/domain/goals/keyResultTypes';
import type { Goal, GoalHorizon, GoalStatus, ID, KeyResult, LocalDate } from '@/domain/types';

export type GoalDraft = Pick<Goal, 'title' | 'horizon' | 'periodStart' | 'periodEnd'>
  & Partial<Pick<Goal, 'areaId' | 'why' | 'parentGoalId' | 'keyResults' | 'status' | 'deadline' | 'deadlineTime' | 'priority'>>;

/** Leeres Key Result fuer das Formular - noch nicht gespeichert. */
export function emptyKeyResult(): KeyResult {
  return { id: newId(), title: '', type: 'zahl', unit: '', start: 0, target: 0, current: 0 };
}

/** Reihenfolge der Horizonte von grob nach fein - so wird auch gruppiert. */
export const HORIZON_ORDER: GoalHorizon[] = ['jahr', 'quartal', '12wochen'];

export async function listGoals(): Promise<Goal[]> {
  const all = await db.goals.toArray();
  return all.sort(
    (a, b) =>
      HORIZON_ORDER.indexOf(a.horizon) - HORIZON_ORDER.indexOf(b.horizon)
      || a.periodStart.localeCompare(b.periodStart)
      || a.title.localeCompare(b.title, 'de'),
  );
}

/**
 * Ziele, die gerade laufen: nicht abgeschlossen und der heutige Tag liegt im
 * Zeitraum. Das ist die Menge fuer das Startseiten-Widget.
 */
export async function activeGoals(today: LocalDate = heute()): Promise<Goal[]> {
  const all = await listGoals();
  return all.filter(
    (g) =>
      (g.status === 'offen' || g.status === 'laeuft')
      && containsDate({ start: g.periodStart, end: g.periodEnd }, today),
  );
}

export async function createGoal(draft: GoalDraft): Promise<Goal> {
  const ts = nowInstant();
  const goal: Goal = {
    id: newId(),
    areaId: draft.areaId ?? null,
    title: draft.title.trim(),
    why: draft.why?.trim() || undefined,
    horizon: draft.horizon,
    parentGoalId: draft.parentGoalId,
    periodStart: draft.periodStart,
    periodEnd: draft.periodEnd,
    keyResults: draft.keyResults ?? [],
    priority: draft.priority ?? DEFAULT_PROJECT_PRIORITY,
    // Nur gesetzte Felder speichern; eine Uhrzeit ohne Datum ist keine Deadline.
    ...(draft.deadline ? { deadline: draft.deadline } : {}),
    ...(draft.deadline && draft.deadlineTime ? { deadlineTime: draft.deadlineTime } : {}),
    status: draft.status ?? 'laeuft',
    createdAt: ts,
    updatedAt: ts,
  };
  await db.goals.add(goal);
  return goal;
}

export async function updateGoal(
  id: ID,
  patch: Partial<Omit<Goal, 'id' | 'createdAt'>>,
): Promise<void> {
  // Wechselt der Bereich, ziehen Projekte und Aufgaben mit (Roadmap F-11).
  if (!('areaId' in patch)) {
    await db.goals.update(id, { ...patch, updatedAt: nowInstant() });
    return;
  }
  await db.transaction('rw', db.areas, db.goals, db.projects, db.tasks, async () => {
    await db.goals.update(id, { ...patch, updatedAt: nowInstant() });
    await applyLinkRule();
  });
}

/** Notizen der Detailseite (F-12). Leer = Feld weg, wie bei Projekten. */
export async function setGoalNotes(id: ID, notes: string): Promise<void> {
  await db.goals.where('id').equals(id).modify((g) => {
    if (notes.trim()) g.notes = notes;
    else delete g.notes;
    g.updatedAt = nowInstant();
  });
}

export async function setGoalStatus(id: ID, status: GoalStatus): Promise<void> {
  await updateGoal(id, { status });
}

/**
 * Loescht ein Ziel und haengt alles ab, was daran hing.
 *
 * Kindziele, Projekte und direkt zugeordnete Aufgaben werden bewusst NICHT
 * mitgeloescht: sie koennen fuer sich stehen, und stillschweigend
 * Mitgeloeschtes waere Datenverlust ohne Rueckfrage. Sie verlieren nur ihren
 * Verweis - frueher blieb er bei Projekten stehen und zeigte ins Leere.
 * Ihren Bereich behalten sie, damit sie in gefilterten Ansichten bleiben.
 */
export async function deleteGoal(id: ID): Promise<void> {
  // Papierkorb (F-45): beim Wiederherstellen haengt alles wieder am Ziel.
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'goals', zeileIds: [id] }, () => db.transaction('rw', [db.goals, db.projects, db.tasks, db.focusGoals, db.books, db.resources, db.thoughts], async () => {
    const ts = nowInstant();
    // Notizen und Gedanken (F-17) bleiben, verlieren nur den Verweis.
    await db.resources.where('goalId').equals(id).modify((r) => { delete r.goalId; r.updatedAt = ts; });
    await db.thoughts.where('goalId').equals(id).modify((g) => { delete g.goalId; g.updatedAt = ts; });
    // Verknuepftes Wissen (F-19) bleibt, verliert nur dieses Ziel.
    await db.books.where('goalIds').equals(id).modify((b) => {
      b.goalIds = (b.goalIds ?? []).filter((x) => x !== id);
      b.updatedAt = ts;
    });
    // Wochen- und Monatsziele (F-16) bleiben, verlieren nur den Verweis.
    await db.focusGoals.where('goalId').equals(id).modify((f) => {
      delete f.goalId;
      f.updatedAt = ts;
    });
    await db.goals.where('parentGoalId').equals(id).modify((child) => {
      delete child.parentGoalId;
      child.updatedAt = ts;
    });
    await db.projects.where('goalId').equals(id).modify((p) => {
      delete p.goalId;
      p.updatedAt = ts;
    });
    await db.tasks.where('goalId').equals(id).modify((t) => {
      delete t.goalId;
      t.updatedAt = ts;
    });
    await db.goals.delete(id);
  }));
}

/**
 * Setzt den Ist-Wert eines Key Results.
 *
 * Lesen und Schreiben liegen in einer Transaktion, damit zwei schnelle
 * Eingaben nicht gegenseitig das Array ueberschreiben.
 */
export async function updateKeyResultValue(
  goalId: ID,
  keyResultId: ID,
  current: number,
): Promise<void> {
  await db.transaction('rw', db.goals, async () => {
    const goal = await db.goals.get(goalId);
    if (!goal) return;

    // Jede Aktualisierung landet mit Datum im Verlauf (V-06, fuer F-02).
    const keyResults = goal.keyResults.map((kr) =>
      kr.id === keyResultId ? mitMessung(kr, current, heute()) : kr,
    );
    await db.goals.update(goalId, { keyResults, updatedAt: nowInstant() });
  });
}

/** Moegliche Elternziele fuer ein Ziel: Jahresziele ausser dem Ziel selbst. */
export function possibleParents(all: Goal[], self?: Goal): Goal[] {
  return all.filter((g) => g.horizon === 'jahr' && g.id !== self?.id);
}
